import React, { useEffect, useState } from "react"
import { OrganizationRequired } from "components/UserManagement/interfaces"
import { Checkbox, Heading } from "@walmart-web/livingdesign-components"
import { FormattedMessage } from "react-intl"
import "./index.scss"
import { OrganizationFields } from "../Users/utils"
import { useFetchData } from "components/Shared/Hooks/react-query/useFetchData"
import {
	getVendors,
	getCategories,
	getPlans,
	getBrands,
} from "components/UserManagement/service"
import { useIntl } from "react-intl"
import Skeleton from "./Skeleton"
import {
	debouncedTime,
	initialDisabledData,
	initialFormData,
} from "./constants"
import useDebounce from "components/Shared/Hooks/useDebounce"
import Input from "../Users/Input"
import { isEmptyString, max100Characters } from "../Validators/validators"
import SelectWithValidator from "../Validators/SelectWithValidator/SelectWithValidator"
import ErrorMessage from "../Validators/ErrorMessage"
import { isSomeTrue } from "helpers/objects"
import { Tuple } from "components/Shared/Interfaces"

interface FormOrganizationProps {
	data?: OrganizationRequired
	setData?: (data: OrganizationRequired) => void
	initialValue?: boolean
	disabled?: Record<OrganizationFields, boolean>
}

const onChangeCheckbox = (items: string[], item: string) => {
	const set = new Set(items)
	set.has(item) ? set.delete(item) : set.add(item)
	return Array.from(set)
}

const FormOrganizations: React.FC<FormOrganizationProps> = ({
	data = initialFormData,
	setData,
}) => {
	const [formData, setFormData] = useState<OrganizationRequired>(data)
	const [disabledField, setDisabledField] = useState<
		Record<OrganizationFields, boolean>
	>(initialDisabledData)

	const vendorDebounced = useDebounce(
		formData.vendors.map((item: Tuple) => item.code).join(),
		debouncedTime
	)

	const intl = useIntl()
	const enabledDebounced = vendorDebounced !== ""
	const initialSelectData = { filter: [] }

	const {
		data: vendorsData = initialSelectData,
		isFetching: isFetchingSuppliers,
	} = useFetchData("retrieveVendors", getVendors)

	const { data: plansData = [], isFetching: isFetchingPlans } = useFetchData(
		"retrievePlans",
		getPlans
	)

	const {
		data: brandsData = initialSelectData,
		isFetching: isFetchingBrands,
	} = useFetchData(
		"retrieveBrands",
		getBrands,
		{ vendors: vendorDebounced },
		{ enabled: enabledDebounced }
	)

	const {
		data: categoriesData = initialSelectData,
		isFetching: isFetchingCategories,
	} = useFetchData(
		"retrieveCategories",
		getCategories,
		{ vendors: vendorDebounced },
		{ enabled: enabledDebounced }
	)

	useEffect(() => {
		if (setData && JSON.stringify(data) !== JSON.stringify(formData)) {
			setData(formData)
		}
	}, [formData])

	useEffect(() => {
		const isDisabled = formData.vendors.length <= 0
		setDisabledField({
			...disabledField,
			brands: isDisabled,
			categories: isDisabled,
		})

		setFormData({
			...formData,
			brands: [],
			categories: [],
		})
	}, [formData.vendors])

	if (isSomeTrue(isFetchingSuppliers, isFetchingPlans)) return <Skeleton />

	return (
		<div className="form-organizations">
			<div className="row">
				<div className="text-field">
					<Heading as="h5" UNSAFE_className="title">
						<FormattedMessage id="phronesis.usermanagement.organizations.name" />
					</Heading>
					<Input
						id="name"
						value={formData.name}
						data={formData}
						setData={setFormData}
						disabled={disabledField.name}
						validators={[isEmptyString, max100Characters]}
					/>
				</div>
				<div className="text-field">
					<Heading as="h5" UNSAFE_className="title">
						<FormattedMessage id="phronesis.usermanagement.organizations.description" />
					</Heading>
					<Input
						id="description"
						value={formData.description}
						data={formData}
						setData={setFormData}
						disabled={disabledField.description}
						validators={[isEmptyString, max100Characters]}
					/>
				</div>
			</div>
			<div className="row">
				<SelectWithValidator
					id={"vendors"}
					data={formData}
					setData={setFormData}
					options={vendorsData.filter}
				/>
				<div className="checkbox-list">
					<Heading as="h5" UNSAFE_className="title">
						<FormattedMessage id="phronesis.usermanagement.organizations.plan" />
					</Heading>
					{plansData.plans?.map((item: Tuple) => (
						<Checkbox
							key={item.name}
							label={intl.formatMessage({
								id: `phronesis.usermanagement.organizations.plan.${item.name}`,
							})}
							className="checkbox"
							checked={formData.plans.includes(item.name)}
							onChange={() =>
								setFormData({
									...formData,
									plans: onChangeCheckbox(formData.plans, item.name),
								})
							}
							disabled={disabledField.plans}
						/>
					))}
					<ErrorMessage items={formData.plans} />
				</div>
			</div>
			<div className="row">
				<SelectWithValidator
					id={"brands"}
					data={formData}
					setData={setFormData}
					options={brandsData.filter}
					isDisabled={disabledField.brands}
					isLoading={isFetchingBrands}
				/>
				<SelectWithValidator
					id={"categories"}
					data={formData}
					setData={setFormData}
					options={categoriesData.filter}
					isDisabled={disabledField.categories}
					isLoading={isFetchingCategories}
				/>
			</div>
		</div>
	)
}

export default FormOrganizations
